import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { FaArrowRight, FaGlobe } from 'react-icons/fa';
import { TimeContext } from '../TimeContext';
import axios from 'axios';
import './Homepage.css';
import { SiteHeader, SiteFooter } from './Homepage';

/* Real testimonial data built from alumni profile */
const testimonials = [
  { quote:'San Tomas gave me the language skills and cultural awareness to thrive at an international hotel in Dubai. The faculty truly prepared me for global work.', name:'Amina Oduya', cohort:'Class of 2018', role:'Guest Relations Manager, Atlantis Dubai', initials:'AO', color:'#4169E1' },
  { quote:'The Air Ticketing program transformed my career. I now fly the world as a senior ticketing officer at Kenya Airways.', name:'Beatrice Wambui', cohort:'Class of 2020', role:'Senior Ticketing Officer, Kenya Airways', initials:'BW', color:'#D97706' },
  { quote:'Learning Mandarin here opened doors I never thought possible. I now work as a trade liaison with Chinese investment partners.', name:'David Kimani', cohort:'Class of 2017', role:'Trade Liaison, Nairobi Business District', initials:'DK', color:'#5a9200' },
  { quote:'The hospitality program gave me practical skills from day one. I moved from student to hotel manager in under three years.', name:'James Mwangi', cohort:'Class of 2016', role:'Hotel Operations Manager, Serena Nairobi', initials:'JM', color:'#7C3AED' },
  { quote:'Prof. Chibeu\'s guidance shaped how I communicate across cultures. I now work as an interpreter for the African Union.', name:'Grace Achieng', cohort:'Class of 2019', role:'AU Interpreter, Addis Ababa', initials:'GA', color:'#0891B2' },
  { quote:'San Tomas gave me the IT and business skills to launch my own tourism tech startup. The practical training is second to none.', name:'Peter Ngugi', cohort:'Class of 2021', role:'Co-Founder, SafariTech Kenya', initials:'PN', color:'#1E3A8A' },
];

const globalPresence = [
  { city:'Nairobi', country:'Kenya', count:3200 },
  { city:'Mombasa', country:'Kenya', count:1400 },
  { city:'Dubai', country:'UAE', count:680 },
  { city:'London', country:'United Kingdom', count:420 },
  { city:'Addis Ababa', country:'Ethiopia', count:310 },
  { city:'Dar es Salaam', country:'Tanzania', count:380 },
  { city:'Kigali', country:'Rwanda', count:240 },
  { city:'Toronto', country:'Canada', count:190 },
];

const Alumni = () => {
  const { t, i18n } = useTranslation();
  const currentTime = useContext(TimeContext);
  const [email, setEmail] = useState('');
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [error, setError] = useState('');
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const navLinks = [
    { to:'/',            label:t('nav.home','Home'),        ariaLabel:t('nav.home','Home') },
    { to:'/programs',    label:t('nav.programs','Programs'),ariaLabel:t('nav.programs','Programs') },
    { to:'/campus-life', label:t('nav.campusLife','Campus Life'), ariaLabel:t('nav.campusLife','Campus Life') },
    { to:'/alumni',      label:t('nav.alumni','Alumni'),    ariaLabel:t('nav.alumni','Alumni') },
    { to:'/enrollment',  label:t('nav.enrollment','Enrollment'), ariaLabel:t('nav.enrollment','Enrollment') },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial(p => (p + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const handleNewsletterSubmit = async e => {
    e.preventDefault();
    if (!email) { setError(t('alumni.emailRequired','Email is required')); return; }
    if (!/\S+@\S+\.\S+/.test(email)) { setError(t('alumni.emailInvalid','Invalid email address')); return; }

    const apiUrl = process.env.REACT_APP_API_URL || 'https://santomas-backend.up.railway.app';
    if (!apiUrl) { setError(t('alumni.submitError','Configuration error: API URL is missing')); return; }

    try {
      await axios.post(`${apiUrl}/api/alumni-newsletter`, { email }, {
        timeout: 10000, headers: { 'Content-Type': 'application/json' }
      });
      setError('');
      setSubmissionSuccess(true);
      setEmail('');
      setTimeout(() => setSubmissionSuccess(false), 5000);
    } catch (err) {
      setError(err.response?.data?.message || t('alumni.submitError','Failed to subscribe. Please try again.'));
    }
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{t('alumni.pageTitle','Alumni - San Tomas Tourism College')}</title>
        <meta name="description" content="Connect with the global alumni network of San Tomas Tourism College." />
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&family=Fraunces:ital,wght@0,700;0,900;1,700;1,900&display=swap" rel="stylesheet" />
        <link rel="canonical" href="https://santomastourismcollege.com/alumni" />
      </Helmet>

      <a href="#main-content" className="sr-only focus:not-sr-only">{t('a11y.skipToContent','Skip to main content')}</a>

      <SiteHeader t={t} i18n={i18n} navLinks={navLinks} />

      {/* ── HERO ── */}
      <section className="sttc-hero" style={{ minHeight:'44vh' }}>
        <div className="hero-grid" aria-hidden="true" />
        <div className="hero-geo" aria-hidden="true" />
        <div className="hero-circle-1" aria-hidden="true" />
        <div className="sttc-container" style={{ position:'relative', zIndex:2, paddingTop:130, paddingBottom:64 }}>
          <motion.div initial={{ opacity:0, y:30 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.7 }}>
            <div className="section-eyebrow" style={{ color:'rgba(251,191,36,0.9)' }}>10,000+ Graduates Worldwide</div>
            <h1 className="section-title" style={{ color:'#fff', fontSize:'clamp(38px,5vw,62px)' }}>
              The <em style={{ color:'#FBBF24' }}>Alumni</em> Network
            </h1>
            <p style={{ fontSize:16, color:'rgba(255,255,255,0.78)', maxWidth:560, lineHeight:1.8, fontWeight:300, marginTop:14 }}>
              {t('alumni.description','Our alumni span the globe, excelling in hospitality, business, IT, and languages. Driven by "Illuminatio et Salus Populi," they light up the world.')}
            </p>
          </motion.div>
        </div>
      </section>

      <div className="sttc-stripe" />

      {/* ── STATS ── */}
      <section style={{ background:'linear-gradient(135deg, #1E3A8A, #2851d3)', padding:'0' }}>
        <div className="sttc-container">
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px,1fr))', gap:0 }}>
            {[
              { n:'10,000+', label:'Total Graduates' },
              { n:'15+',     label:'Countries' },
              { n:'94%',     label:'Employment Rate' },
              { n:'1997',    label:'Est.' },
            ].map((s,i) => (
              <div key={i} style={{ textAlign:'center', padding:'44px 16px', borderRight: i < 3 ? '1px solid rgba(255,255,255,0.1)' : 'none' }}>
                <div style={{ fontFamily:"'Fraunces',serif", fontSize:'clamp(32px,4vw,48px)', fontWeight:900, color:'#FBBF24', lineHeight:1 }}>{s.n}</div>
                <div style={{ fontSize:11, color:'rgba(255,255,255,0.6)', letterSpacing:'0.12em', textTransform:'uppercase', marginTop:10 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="sttc-stripe" />

      {/* ── FEATURED TESTIMONIAL CAROUSEL ── */}
      <section id="main-content" className="sttc-section faculty-section-bg">
        <div className="sttc-container">
          <div className="text-center mb-8">
            <div className="section-eyebrow">Success Stories</div>
            <h2 className="section-title">Voices of Our <em>Graduates</em></h2>
          </div>

          {/* Featured rotating quote */}
          <div style={{ maxWidth:760, margin:'0 auto 56px', textAlign:'center' }}>
            {testimonials.map((test, i) => (
              <div key={i} style={{ display: activeTestimonial===i ? 'block' : 'none' }}>
                <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.6 }}>
                  <div style={{ fontFamily:"'Fraunces',serif", fontSize:72, color:'rgba(251,191,36,0.2)', lineHeight:0.8, marginBottom:-10 }}>"</div>
                  <p style={{ fontFamily:"'Fraunces',serif", fontSize:'clamp(18px,2.4vw,24px)', color:'#fff', lineHeight:1.65, fontStyle:'italic', marginBottom:32 }}>
                    {test.quote}
                  </p>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:14 }}>
                    <div style={{ width:48, height:48, borderRadius:'50%', background:test.color, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'Fraunces',serif", fontSize:16, color:'#fff', fontWeight:700, flexShrink:0 }}>
                      {test.initials}
                    </div>
                    <div style={{ textAlign:'left' }}>
                      <div style={{ fontSize:14, color:'#FBBF24', fontWeight:600 }}>{test.name} · {test.cohort}</div>
                      <div style={{ fontSize:12, color:'rgba(255,255,255,0.6)', marginTop:2 }}>{test.role}</div>
                    </div>
                  </div>
                </motion.div>
              </div>
            ))}
            <div style={{ display:'flex', justifyContent:'center', gap:8, marginTop:28 }}>
              {testimonials.map((_,i) => (
                <button key={i} onClick={() => setActiveTestimonial(i)}
                  style={{ width: activeTestimonial===i ? 28 : 8, height:8, borderRadius:4, background: activeTestimonial===i ? '#FBBF24' : 'rgba(255,255,255,0.2)', border:'none', cursor:'pointer', transition:'all .4s', padding:0 }} />
              ))}
            </div>
          </div>

          {/* All testimonials grid */}
          <div className="faculty-grid">
            {testimonials.map((test, i) => (
              <motion.div
                key={i}
                className="alumni-testimonial-card"
                initial={{ opacity:0, y:32 }} whileInView={{ opacity:1, y:0 }}
                viewport={{ once:true }} transition={{ duration:0.5, delay:(i%3)*0.08 }}
              >
                <div style={{ height:3, background:`linear-gradient(90deg, ${test.color}, rgba(255,255,255,0.1))`, marginBottom:20, borderRadius:2 }} />
                <span className="quote-mark">"</span>
                <p style={{ fontSize:14, color:'rgba(255,255,255,0.78)', lineHeight:1.75, fontStyle:'italic', marginBottom:20 }}>{test.quote}</p>
                <div style={{ display:'flex', alignItems:'center', gap:12, paddingTop:16, borderTop:'1px solid rgba(255,255,255,0.1)' }}>
                  <div style={{ width:40, height:40, borderRadius:'50%', background:test.color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, color:'#fff', fontWeight:700, flexShrink:0 }}>
                    {test.initials}
                  </div>
                  <div>
                    <div style={{ fontSize:13, color:'#fff', fontWeight:600 }}>{test.name}</div>
                    <div style={{ fontSize:11, color:'rgba(255,255,255,0.5)', marginTop:2 }}>{test.cohort} · {test.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <div className="sttc-stripe" />

      {/* ── GLOBAL PRESENCE ── */}
      <section className="sttc-section white">
        <div className="sttc-container">
          <div className="text-center mb-8">
            <div className="section-eyebrow">Where We Are</div>
            <h2 className="section-title">Global <em>Presence</em></h2>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px,1fr))', gap:16 }}>
            {globalPresence.map((place, i) => (
              <motion.div
                key={i}
                initial={{ opacity:0, scale:0.95 }} whileInView={{ opacity:1, scale:1 }}
                viewport={{ once:true }} transition={{ duration:0.4, delay:i*0.05 }}
                style={{ background:'linear-gradient(135deg, #EEF1FC, #fff)', border:'1px solid rgba(65,105,225,0.12)', borderRadius:12, padding:'20px 22px', display:'flex', gap:14, alignItems:'center' }}
              >
                <div style={{ width:40, height:40, borderRadius:'50%', background:'rgba(65,105,225,0.1)', border:'1px solid rgba(65,105,225,0.2)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <FaGlobe style={{ color:'#4169E1', fontSize:14 }} />
                </div>
                <div>
                  <div style={{ fontFamily:"'Fraunces',serif", fontSize:15, color:'#1E3A8A', fontWeight:700 }}>{place.city}</div>
                  <div style={{ fontSize:11, color:'#6B7280', marginTop:2 }}>{place.country}</div>
                  <div style={{ fontSize:11, color:'#D97706', marginTop:4, fontWeight:600 }}>{place.count.toLocaleString()} alumni</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <div className="sttc-stripe" />

      {/* ── NEWSLETTER ── */}
      <section className="sttc-section tinted">
        <div className="sttc-container">
          <div className="alumni-newsletter">
            <h3>Join the Alumni Network</h3>
            <p>Subscribe for alumni news, networking events, mentorship opportunities and exclusive updates.</p>
            {submissionSuccess ? (
              <div style={{ background:'rgba(30,58,138,0.12)', borderRadius:12, padding:'20px 24px', maxWidth:420, margin:'0 auto' }}>
                <div style={{ fontSize:32, marginBottom:8 }}>🎉</div>
                <p style={{ fontSize:14, color:'#1E3A8A', fontWeight:600 }}>
                  {t('alumni.subscribeSuccess','Thanks for subscribing! We\'ll be in touch soon.')}
                </p>
              </div>
            ) : (
              <form onSubmit={handleNewsletterSubmit} className="newsletter-form">
                <input
                  type="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setError(''); }}
                  placeholder={t('alumni.emailPlaceholder','Enter your email address')}
                  className="newsletter-input"
                  aria-label="Email address for alumni newsletter"
                  required
                />
                <motion.button
                  type="submit"
                  className="btn-royal"
                  style={{ borderRadius:50, fontSize:13, padding:'13px 24px' }}
                  whileHover={{ scale:1.03 }} whileTap={{ scale:0.97 }}
                >
                  Subscribe <FaArrowRight />
                </motion.button>
              </form>
            )}
            {error && <p style={{ color:'#EF4444', fontSize:13, marginTop:12 }}>{error}</p>}
          </div>
        </div>
      </section>

      <SiteFooter t={t} currentTime={currentTime} />
    </div>
  );
};

export default Alumni;